import React from "react";

import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation } from "@apollo/client";
import { GET_PACKAGES_QUERY } from "../../graphql/queries/packages";
import { useEffect, useState } from "react";
import { GET_TOWERS_QUERY } from "../../graphql/queries/towers";
import Loader from "../loader/loader";
import { toastFunction, errorToastFunction } from "../../utils/toast";
import { formatDateForInput } from "../../utils/date-formatter";
import { depositMapping } from "../../utils/deposit-mapping";
import { UPDATE_CLIENT_MUTATION } from "../../graphql/mutations/updateClientMutation";
import { UPDATE_MY_PROFILE_MUTATION } from "../../graphql/mutations/updateMyProfileMutation";
import FloatingLabelInput from "../FloatingLabelInput/FloatingLabelInput";
import SingleSelect from "../single-select/single-select";
import DisplaySelect from "../display-select/display-select";

const EditClient = ({
  onCancel,
  clientData,
  clientId,
  clientResponse,
  fieldsDisabled = false,
  type = "Client",
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      channel: clientData.channel,
      first_name: clientData.name,
      surname: clientData.surname,
      sa_id: clientData.saId,
      address: clientData.w3w,
      phone_number: clientData.phoneNumber,
      balance: clientData.balance,
      client_id: clientData.clientId,
      package_id: {
        label: clientData.package.name,
        value: clientData.package.id,
      },
      zonke_id: {
        label: clientData.tower.zonkeId,
        value: clientData.tower.zonkeId,
      },
      contact_method: {
        label: clientData.contactMethod,
        value: clientData.contactMethod.toLowerCase(),
      },
      expiry_date: formatDateForInput(clientData.expiryDate),
      active: clientData.active,
      deposit_paid: {
        label: depositMapping[clientData.depositPaid] ? "Yes" : "No",
        value: depositMapping[clientData.depositPaid],
      },
      is_host: {
        label: clientData.isHost ? "Yes" : "No",
        value: clientData.isHost,
      },
    },
  });

  // A client editing their own profile (fieldsDisabled) can't change package or
  // tower, and isn't allowed to read the towers list, so skip both lookups.
  const getPackagesResponse = useQuery(GET_PACKAGES_QUERY, {
    fetchPolicy: "cache-and-network",
    skip: fieldsDisabled,
  });

  const getTowersResponse = useQuery(GET_TOWERS_QUERY, {
    fetchPolicy: "cache-and-network",
    skip: fieldsDisabled,
  });

  const [updateClient, { loading: updateClientLoading }] = useMutation(
    UPDATE_CLIENT_MUTATION
  );
  const [updateMyProfile, { loading: updateProfileLoading }] = useMutation(
    UPDATE_MY_PROFILE_MUTATION
  );
  const loading = updateClientLoading || updateProfileLoading;

  const [packages, setPackages] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [towers, setTowers] = useState(null);

  useEffect(() => {
    if (getPackagesResponse?.data) {
      const data = getPackagesResponse.data.packages.items.map((p) => ({
        value: p.id,
        label: p.name,
      }));
      setPackages(data);
    }

    if (getTowersResponse.data) {
      const data = getTowersResponse.data.towers.items.map((t) => ({
        value: t.zonkeId,
        label: t.zonkeId,
      }));

      setTowers(data);
    }
  }, [getPackagesResponse, getTowersResponse]);

  if (getPackagesResponse.loading || getTowersResponse.loading || loading) {
    return <Loader sizeClass={"size--medium"} />;
  }

  if (getPackagesResponse.error || getTowersResponse.error) {
    return (
      <div className="basic-modal">
        <div className="basic-modal__header">
          <h3>Error Loading Data</h3>
        </div>
        <div className="basic-modal__body">
          <div
            className="input-container__error"
            style={{
              padding: "20px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
            }}
          >
            {getPackagesResponse.error?.message ||
              getTowersResponse.error?.message ||
              "Failed to load packages or towers. Please try again."}
          </div>
          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (data) => {
    const clientData = {
      channel: parseInt(data.channel),
      name: data.first_name,
      zonke_id: data.zonke_id.value,
      sa_id: data.sa_id,
      surname: data.surname,
      w3w: data.address,
      deposit_paid: data.deposit_paid.value,
      package_id: parseInt(data.package_id.value),
      phone_number: data.phone_number,
      contact_method: data.contact_method.value,
      balance: parseFloat(data.balance),
      expiry_date: data.expiry_date,
      active: data.active,
      is_host: data.is_host.value,
      client_id: data.client_id ? parseInt(data.client_id) : null,
    };
    try {
      setErrorMessage("");
      // Clients can only change their own contact details, through their own mutation.
      const updatedClient = fieldsDisabled
        ? (
            await updateMyProfile({
              variables: {
                profileData: {
                  name: clientData.name,
                  surname: clientData.surname,
                  phone_number: clientData.phone_number,
                  contact_method: clientData.contact_method,
                },
              },
            })
          ).data.updateMyProfile.client
        : (
            await updateClient({
              variables: { clientData: clientData, id: clientId },
            })
          ).data.updateClient.client;
      toastFunction(
        `Successfully updated client with name ${updatedClient.name}.`,
        "success",
        "client-update-success"
      );
      onCancel();
      clientResponse.refetch();

      reset();
    } catch (error) {
      console.error("Update client error:", error);
      console.error("GraphQL Errors:", error.graphQLErrors);
      console.error("Network Error:", error.networkError);
      console.error("Error Message:", error.message);

      // Extract error message from GraphQL errors or network errors
      let displayError = "Failed to update client. Please try again.";

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0].message;
        console.log("GraphQL Error Message:", graphQLError);

        // Clean up database error messages for better readability
        if (graphQLError.includes("Duplicate entry")) {
          const match = graphQLError.match(/Duplicate entry '(.+?)' for key/);
          if (match) {
            const parts = match[1].split("-");
            displayError = `This tower (${parts[0]}) and channel (${parts[1]}) combination is already assigned to another client.`;
          } else {
            displayError =
              "This tower and channel combination already exists for another client.";
          }
        } else {
          // Show the raw GraphQL error if it's not a duplicate entry
          displayError = graphQLError;
        }
      } else if (error.networkError) {
        displayError = `Network error: ${error.networkError.message}`;
      } else if (error.message) {
        displayError = error.message;
      }

      console.log("Final display error:", displayError);
      setErrorMessage(displayError);
      errorToastFunction(displayError, "client-update-error");
    }
  };

  return (
    <div className="basic-modal">
      <div className="basic-modal__header">
        <h3>Edit {type}</h3>
      </div>
      <div className="basic-modal__body">
        {errorMessage && (
          <div
            className="input-container__error"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
              fontSize: "14px",
              lineHeight: "1.5",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {errorMessage}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="basic-modal__form">
          {!fieldsDisabled && (
            <FloatingLabelInput
              type={"number"}
              className={""}
              disabled={fieldsDisabled}
              name={"channel"}
              register={register}
              rules={{ required: false }}
              placeholder={"Channel"}
              errors={errors}
            />
          )}
          {!fieldsDisabled && (
            <div className="input-container">
              <DisplaySelect
                placeholder={"Is Host"}
                name={"is_host"}
                errors={errors}
              >
                <Controller
                  name={"is_host"}
                  rules={{
                    required: false,
                  }}
                  control={control}
                  render={({ field }) => (
                    <SingleSelect
                      field={field}
                      options={[
                        { value: false, label: "No" },
                        { value: true, label: "Yes" },
                      ]}
                    />
                  )}
                />
              </DisplaySelect>
            </div>
          )}

          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"first_name"}
            register={register}
            rules={{ required: "The first name field is required." }}
            placeholder={"First Name"}
            errors={errors}
          />

          {!fieldsDisabled && (
            <FloatingLabelInput
              type={"number"}
              className={""}
              disabled={false}
              name={"client_id"}
              register={register}
              rules={{
                required: false,
                min: {
                  value: 100000,
                  message: "Client ID must be at least 100000",
                },
                max: {
                  value: 999999,
                  message: "Client ID must be at most 999999",
                },
              }}
              placeholder={"Client ID (Optional)"}
              errors={errors}
            />
          )}

          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"surname"}
            register={register}
            rules={{ required: "The surname field is required." }}
            placeholder={"Surname"}
            errors={errors}
          />

          {!fieldsDisabled && (
            <FloatingLabelInput
              type={"text"}
              className={""}
              disabled={fieldsDisabled}
              name={"sa_id"}
              register={register}
              rules={{ required: "The South African ID field is required." }}
              placeholder={"South African ID"}
              errors={errors}
            />
          )}

          {!fieldsDisabled && (
            <FloatingLabelInput
              type={"text"}
              className={""}
              disabled={fieldsDisabled}
              name={"address"}
              register={register}
              rules={{ required: false }}
              placeholder={"Address"}
              errors={errors}
            />
          )}

          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"phone_number"}
            register={register}
            rules={{ required: false }}
            placeholder={"Phone Number"}
            errors={errors}
          />

          {!fieldsDisabled && (
            <FloatingLabelInput
              type={"number"}
              className={""}
              disabled={false}
              name={"balance"}
              register={register}
              rules={{ required: false }}
              placeholder={"Balance"}
              errors={errors}
            />
          )}

          {!fieldsDisabled && (
            <div className="input-container">
              <DisplaySelect
                placeholder={"Deposit Paid"}
                name={"deposit_paid"}
                errors={errors}
              >
                <Controller
                  name={"deposit_paid"}
                  rules={{
                    required: false,
                  }}
                  control={control}
                  render={({ field }) => (
                    <SingleSelect
                      field={field}
                      options={[
                        { value: 0, label: "No" },
                        { value: 1, label: "Yes" },
                      ]}
                    />
                  )}
                />
              </DisplaySelect>
            </div>
          )}

          {!fieldsDisabled && (
            <div className="input-container">
              <DisplaySelect
                placeholder={"Package"}
                name={"package_id"}
                errors={errors}
              >
                <Controller
                  name={"package_id"}
                  rules={{
                    required: "The Package field is required.",
                  }}
                  control={control}
                  render={({ field }) => (
                    <SingleSelect field={field} options={packages} />
                  )}
                />
              </DisplaySelect>
            </div>
          )}

          {!fieldsDisabled && (
            <div className="input-container">
              <DisplaySelect
                placeholder={"Tower"}
                name={"zonke_id"}
                errors={errors}
              >
                <Controller
                  name={"zonke_id"}
                  rules={{
                    required: "The Tower field is required.",
                  }}
                  control={control}
                  render={({ field }) => (
                    <SingleSelect field={field} options={towers} />
                  )}
                />
              </DisplaySelect>
            </div>
          )}

          <div className="input-container">
            <DisplaySelect
              placeholder={"Contact Method"}
              name={"contact_method"}
              errors={errors}
            >
              <Controller
                name={"contact_method"}
                rules={{
                  required: "The contact method field is required.",
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect
                    field={field}
                    options={[
                      { value: "app", label: "Mobile App" },
                      { value: "sms", label: "SMS" },
                    ]}
                  />
                )}
              />
            </DisplaySelect>
          </div>

          {!fieldsDisabled && (
            <FloatingLabelInput
              type="datetime-local"
              className={""}
              disabled={false}
              name={"expiry_date"}
              register={register}
              rules={{ required: false }}
              placeholder={"Expiry Date"}
              errors={errors}
            />
          )}

          {!fieldsDisabled && (
            <div className="input-container input-container__checkmark">
              <label htmlFor="input-container__label">Active</label>
              <input type="checkbox" {...register("active")} />
            </div>
          )}

          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Cancel
            </button>
            <button type="submit" className="button button--primary">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClient;
